import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Key, AlertTriangle, Activity, Settings,
  Check, X, RefreshCw, Loader2, Eye, EyeOff,
  Lock, Unlock, Power, PowerOff, ChevronRight,
  Calendar, Filter, Download, Trash2, Clock,
  ToggleLeft, ToggleRight, AlertCircle, Info,
  Database, MessageSquare, CreditCard, Phone, Zap,
  Users, FileText, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { cn } from '../lib/utils';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab configuration
const TABS = [
  { id: 'integration', label: 'Integration', icon: Key },
  { id: 'policy', label: 'Agent Policy', icon: Shield },
  { id: 'audit', label: 'Audit Log', icon: Activity },
  { id: 'advanced', label: 'Advanced', icon: Settings, adminOnly: true }
];

// Trust Panel security features
const TRUST_FEATURES = [
  { icon: Lock, text: 'API keys are stored securely on the server (never shown in the dashboard)' },
  { icon: Shield, text: 'Credentials are encrypted at rest using AES-256' },
  { icon: Users, text: 'Tenant isolation prevents cross-account access' },
  { icon: Activity, text: 'All actions are logged to audit trail' },
  { icon: PowerOff, text: 'Kill switch available anytime to disable agent' },
  { icon: RefreshCw, text: 'Rotate keys anytime without service interruption' }
];

export default function SecuritySettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('integration');
  const [loading, setLoading] = useState(true);
  
  // Role-based access
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isReadOnly = user?.role === 'standard' || user?.role === 'viewer';
  
  // Integration state
  const [integration, setIntegration] = useState(null);
  const [connectingIntegration, setConnectingIntegration] = useState(false);
  const [verifyingIntegration, setVerifyingIntegration] = useState(false);
  const [disconnectingIntegration, setDisconnectingIntegration] = useState(false);
  const [rotatingKey, setRotatingKey] = useState(false);
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [confirmApiKey, setConfirmApiKey] = useState('');
  const [newLocationId, setNewLocationId] = useState('');
  const [verifyAfterSave, setVerifyAfterSave] = useState(true);
  
  // Policy state
  const [policy, setPolicy] = useState({
    agent_enabled: true,
    confirmation_mode: 'SMART_CONFIRM',
    max_bulk_recipients: 1,
    require_confirm_billable: true,
    require_confirm_bulk: true,
    require_confirm_destructive: true,
    require_confirm_publishing: true
  });
  const [savingPolicy, setSavingPolicy] = useState(false);
  
  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilters, setAuditFilters] = useState({
    date_from: '',
    date_to: '',
    action_type: '',
    status: '',
    risk_level: '',
    system: ''
  });
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [exportingAudit, setExportingAudit] = useState(false);
  
  // Advanced state (admin only)
  const [globalKillSwitch, setGlobalKillSwitch] = useState(false);
  const [showKillSwitchConfirm, setShowKillSwitchConfirm] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [rotationHistory, setRotationHistory] = useState(null);
  const [systemFlags, setSystemFlags] = useState({
    debug_mode: false,
    maintenance_mode: false,
    audit_verbose: true
  });
  
  // Auth headers helper
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };
  
  // Fetch integration status
  const fetchIntegration = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/security/summary`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setIntegration(data.integration);
        setCapabilities(data.capabilities);
        setRotationHistory(data.rotation_history);
        setGlobalKillSwitch(!data.system_flags?.AGENT_EXECUTION_ENABLED);
      }
    } catch (error) {
      console.error('Error fetching integration:', error);
      // Fallback to legacy endpoint
      try {
        const fallback = await fetch(`${API_URL}/api/agent/integration/status`, {
          headers: getAuthHeaders()
        });
        if (fallback.ok) {
          const data = await fallback.json();
          setIntegration(data);
        }
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    }
  }, []);
  
  // Fetch policy
  const fetchPolicy = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/policy`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPolicy(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching policy:', error);
    }
  }, []);
  
  // Fetch audit logs
  const fetchAuditLogs = useCallback(async () => {
    setLoadingAudit(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '50');
      if (auditFilters.status) params.append('status', auditFilters.status);
      if (auditFilters.action_type) params.append('action_type', auditFilters.action_type);
      if (auditFilters.risk_level) params.append('risk_level', auditFilters.risk_level);
      if (auditFilters.system) params.append('system', auditFilters.system);
      if (auditFilters.date_from) params.append('date_from', auditFilters.date_from);
      if (auditFilters.date_to) params.append('date_to', auditFilters.date_to);
      
      const response = await fetch(`${API_URL}/api/audit?${params}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.audit_logs || []);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoadingAudit(false);
    }
  }, [auditFilters]);
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchIntegration(),
        fetchPolicy(),
        fetchAuditLogs()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchIntegration, fetchPolicy, fetchAuditLogs]);
  
  // Connect integration
  const handleConnect = async (apiKey, locationId) => {
    setConnectingIntegration(true);
    try {
      const response = await fetch(`${API_URL}/api/agent/integration/connect`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          api_key: apiKey,
          location_id: locationId
        })
      });
      
      if (response.ok) {
        toast.success('Integration connected successfully');
        await fetchIntegration();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to connect integration');
      }
    } catch (error) {
      toast.error('Error connecting integration');
    } finally {
      setConnectingIntegration(false);
    }
  };
  
  // Verify integration
  const handleVerify = async () => {
    setVerifyingIntegration(true);
    try {
      const response = await fetch(`${API_URL}/api/integrations/verify`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.valid) {
          toast.success('Integration verified successfully');
        } else {
          toast.error(result.message || 'Integration verification failed');
        }
        await fetchIntegration();
      } else {
        toast.error('Verification failed');
      }
    } catch (error) {
      toast.error('Error verifying integration');
    } finally {
      setVerifyingIntegration(false);
    }
  };
  
  // Disconnect integration
  const handleDisconnect = async () => {
    setDisconnectingIntegration(true);
    try {
      const response = await fetch(`${API_URL}/api/integrations/disconnect`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Integration disconnected');
        setIntegration(null);
        await fetchIntegration();
      } else {
        toast.error('Failed to disconnect');
      }
    } catch (error) {
      toast.error('Error disconnecting integration');
    } finally {
      setDisconnectingIntegration(false);
      setShowDisconnectConfirm(false);
    }
  };
  
  // Rotate key
  const handleRotateKey = async () => {
    if (!newApiKey) {
      toast.error('Please enter a new API key');
      return;
    }
    if (newApiKey !== confirmApiKey) {
      toast.error('API keys do not match');
      return;
    }
    
    setRotatingKey(true);
    try {
      const response = await fetch(`${API_URL}/api/integrations/rotate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          token: newApiKey,
          location_id: newLocationId || integration?.location_id,
          verify_after_save: verifyAfterSave
        })
      });
      
      if (response.ok) {
        toast.success('API key rotated successfully');
        setShowRotateModal(false);
        setNewApiKey('');
        setConfirmApiKey('');
        setNewLocationId('');
        await fetchIntegration();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to rotate key');
      }
    } catch (error) {
      toast.error('Error rotating key');
    } finally {
      setRotatingKey(false);
    }
  };
  
  // Save policy
  const handleSavePolicy = async () => {
    setSavingPolicy(true);
    try {
      const response = await fetch(`${API_URL}/api/policy/update`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(policy)
      });
      
      if (response.ok) {
        toast.success('Policy saved successfully');
      } else {
        toast.error('Failed to save policy');
      }
    } catch (error) {
      toast.error('Error saving policy');
    } finally {
      setSavingPolicy(false);
    }
  };
  
  // Toggle agent execution
  const handleToggleAgentExecution = async () => {
    try {
      const response = await fetch(`${API_URL}/api/policy/update`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...policy,
          agent_enabled: !policy.agent_enabled
        })
      });
      
      if (response.ok) {
        setPolicy(prev => ({ ...prev, agent_enabled: !prev.agent_enabled }));
        toast.success(policy.agent_enabled ? 'Agent execution disabled' : 'Agent execution enabled');
      }
    } catch (error) {
      toast.error('Error toggling agent execution');
    }
    setShowKillSwitchConfirm(false);
  };
  
  // Toggle global kill switch (admin only)
  const handleToggleGlobalKillSwitch = async () => {
    try {
      const response = await fetch(`${API_URL}/api/system-flags`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          AGENT_EXECUTION_ENABLED: globalKillSwitch
        })
      });
      
      if (response.ok) {
        setGlobalKillSwitch(!globalKillSwitch);
        toast.success(globalKillSwitch ? 'All agents enabled' : 'All agents disabled');
      }
    } catch (error) {
      toast.error('Error toggling global kill switch');
    }
  };
  
  // Export audit logs
  const handleExportAudit = async () => {
    setExportingAudit(true);
    try {
      const params = new URLSearchParams();
      params.append('format', 'csv');
      if (auditFilters.date_from) params.append('date_from', auditFilters.date_from);
      if (auditFilters.date_to) params.append('date_to', auditFilters.date_to);
      
      const response = await fetch(`${API_URL}/api/audit/export?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_log_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Audit log exported');
      } else {
        toast.error('Failed to export audit log');
      }
    } catch (error) {
      toast.error('Error exporting audit log');
    } finally {
      setExportingAudit(false);
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };
  
  // Render Integration Tab
  const renderIntegrationTab = () => (
    <div className="space-y-6">
      {/* CRM Integration Section */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center",
              integration?.connected ? "bg-green-500/20" : "bg-gray-500/20"
            )}>
              <Database className={cn(
                "w-6 h-6",
                integration?.connected ? "text-green-400" : "text-gray-400"
              )} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">CRM Integration</h3>
              <p className="text-sm text-gray-400">
                {integration?.connected 
                  ? (integration?.is_agency_default ? 'Using Agency CRM' : 'Connected to CRM')
                  : 'Not connected'}
              </p>
            </div>
          </div>
          
          <div className={cn(
            "px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5",
            integration?.connected 
              ? "bg-green-500/20 text-green-400"
              : "bg-red-500/20 text-red-400"
          )}>
            {integration?.connected ? (
              <><CheckCircle className="w-3 h-3" /> Connected</>
            ) : (
              <><XCircle className="w-3 h-3" /> Disconnected</>
            )}
          </div>
        </div>
        
        {integration?.is_agency_default && (
          <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20 mb-4">
            <p className="text-xs text-blue-400 flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <span>You are using the <strong>Agency CRM</strong>. Client accounts can connect their own CRM for independent operation.</span>
            </p>
          </div>
        )}
        {integration?.connected ? (
          <div className="space-y-4">
            {/* Connection details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-black/20 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Location ID</p>
                <p className="text-sm text-white font-mono truncate">
                  {integration?.location_id || 'N/A'}
                </p>
              </div>
              <div className="bg-black/20 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Token (Last 4)</p>
                <p className="text-sm text-white font-mono">
                  ****{integration?.token_last4 || '****'}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-black/20 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Last Verified</p>
                <p className="text-sm text-white">
                  {formatDate(integration?.last_verified)}
                </p>
              </div>
              <div className="bg-black/20 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Permissions</p>
                <p className="text-sm text-white">
                  {integration?.scopes?.length || 0} scopes
                </p>
              </div>
            </div>
            
            {/* Action buttons - Only for admins/owners */}
            {!isReadOnly && (
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button
                  onClick={handleVerify}
                  disabled={verifyingIntegration}
                  variant="outline"
                  className="flex-1"
                  data-testid="verify-connection-btn"
                >
                  {verifyingIntegration ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Verify Connection
                </Button>
                <Button
                  onClick={() => setShowRotateModal(true)}
                  variant="outline"
                  className="flex-1"
                  data-testid="rotate-key-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Rotate API Key
                </Button>
                <Button
                  onClick={() => setShowDisconnectConfirm(true)}
                  disabled={disconnectingIntegration}
                  variant="destructive"
                  className="flex-1"
                  data-testid="disconnect-btn"
                >
                  {disconnectingIntegration ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <X className="w-4 h-4 mr-2" />
                  )}
                  Disconnect
                </Button>
              </div>
            )}
          </div>
        ) : (
          <ConnectIntegrationForm 
            onConnect={handleConnect} 
            connecting={connectingIntegration}
            disabled={isReadOnly}
          />
        )}
      </div>
      
      {/* Trust Panel */}
      <div className="bg-gradient-to-br from-green-500/5 to-cyan-500/5 rounded-xl p-6 border border-green-500/20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Security Trust Panel</h3>
            <p className="text-sm text-gray-400">How we protect your credentials</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {TRUST_FEATURES.map((feature, idx) => (
            <div 
              key={idx}
              className="flex items-start gap-3 p-3 bg-black/20 rounded-lg"
            >
              <feature.icon className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
              <span className="text-sm text-gray-300">{feature.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  // Render Policy Tab
  const renderPolicyTab = () => (
    <div className="space-y-6">
      {/* Agent Execution Toggle */}
      <div className={cn(
        "bg-white/5 rounded-xl p-6 border",
        policy.agent_enabled ? "border-white/10" : "border-red-500/30"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center",
              policy.agent_enabled ? "bg-green-500/20" : "bg-red-500/20"
            )}>
              {policy.agent_enabled ? (
                <Power className="w-6 h-6 text-green-400" />
              ) : (
                <PowerOff className="w-6 h-6 text-red-400" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">AGI Agent Execution</h3>
              <p className="text-sm text-gray-400">
                {policy.agent_enabled 
                  ? 'Agent can execute actions on your behalf'
                  : 'Agent execution is DISABLED (kill switch active)'}
              </p>
            </div>
          </div>
          
          <Switch
            checked={policy.agent_enabled}
            onCheckedChange={(checked) => {
              if (!checked) {
                setShowKillSwitchConfirm(true);
              } else {
                handleToggleAgentExecution();
              }
            }}
            disabled={isReadOnly}
          />
        </div>
        
        {!policy.agent_enabled && (
          <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
            <p className="text-sm text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Agent can chat but cannot execute any tools or actions
            </p>
          </div>
        )}
      </div>
      
      {/* Confirmation Mode */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Confirmation Mode</h3>
        <div className="space-y-3">
          {[
            { value: 'SMART_CONFIRM', label: 'Smart Confirm (Recommended)', desc: 'Only confirm risky actions based on context' },
            { value: 'ALWAYS_CONFIRM', label: 'Always Confirm', desc: 'Require confirmation for every action' }
          ].map(mode => (
            <label 
              key={mode.value}
              className={cn(
                "flex items-center gap-4 p-4 rounded-lg cursor-pointer transition-colors",
                policy.confirmation_mode === mode.value 
                  ? "bg-primary/10 border border-primary/30"
                  : "bg-black/20 border border-transparent hover:border-white/10",
                isReadOnly && "opacity-60 cursor-not-allowed"
              )}
            >
              <input
                type="radio"
                name="confirmation_mode"
                value={mode.value}
                checked={policy.confirmation_mode === mode.value}
                onChange={(e) => !isReadOnly && setPolicy({ ...policy, confirmation_mode: e.target.value })}
                className="sr-only"
                disabled={isReadOnly}
              />
              <div className={cn(
                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                policy.confirmation_mode === mode.value
                  ? "border-primary bg-primary"
                  : "border-gray-500"
              )}>
                {policy.confirmation_mode === mode.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <div>
                <p className="text-white font-medium">{mode.label}</p>
                <p className="text-sm text-gray-400">{mode.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
      
      {/* Risk Controls */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Risk Controls</h3>
        <div className="space-y-4">
          {[
            { key: 'require_confirm_billable', label: 'Require confirmation for Billable Actions', desc: 'SMS, emails, API calls that incur costs', default: true },
            { key: 'require_confirm_bulk', label: 'Require confirmation for Bulk Actions', desc: 'Actions affecting multiple contacts', default: true },
            { key: 'require_confirm_destructive', label: 'Require confirmation for Destructive Actions', desc: 'Delete, archive, or modify existing data', default: true },
            { key: 'require_confirm_publishing', label: 'Require confirmation for Publishing/Activating', desc: 'Workflows, automations, campaigns', default: true }
          ].map(control => (
            <div key={control.key} className="flex items-center justify-between p-4 bg-black/20 rounded-lg">
              <div>
                <p className="text-white font-medium">{control.label}</p>
                <p className="text-sm text-gray-400">{control.desc}</p>
              </div>
              <Switch
                checked={policy[control.key] ?? control.default}
                onCheckedChange={(checked) => !isReadOnly && setPolicy({ ...policy, [control.key]: checked })}
                disabled={isReadOnly}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Bulk Safety Limits */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Bulk Safety Limits</h3>
        <p className="text-sm text-gray-400 mb-4">
          Actions exceeding this limit will require confirmation or be blocked by policy
        </p>
        <div className="flex items-center gap-4">
          <label className="text-sm text-gray-400">Max bulk recipients auto-execute limit:</label>
          <input
            type="number"
            value={policy.max_bulk_recipients}
            onChange={(e) => !isReadOnly && setPolicy({ ...policy, max_bulk_recipients: parseInt(e.target.value) || 1 })}
            min={1}
            max={1000}
            className="w-24 bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white"
            disabled={isReadOnly}
          />
          <span className="text-xs text-gray-500">recipients</span>
        </div>
      </div>
      
      {/* Save Button */}
      {!isReadOnly && (
        <div className="flex justify-end">
          <Button 
            onClick={handleSavePolicy}
            disabled={savingPolicy}
            className="px-8"
            data-testid="save-policy-btn"
          >
            {savingPolicy ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Save Policy
          </Button>
        </div>
      )}
    </div>
  );
  
  // Render Audit Tab
  const renderAuditTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
          <Activity className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Agent Activity Log</h3>
          <p className="text-sm text-gray-400">Track all agent actions and their outcomes</p>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs text-gray-400 mb-1">From Date</label>
            <input
              type="date"
              value={auditFilters.date_from}
              onChange={(e) => setAuditFilters({ ...auditFilters, date_from: e.target.value })}
              className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">To Date</label>
            <input
              type="date"
              value={auditFilters.date_to}
              onChange={(e) => setAuditFilters({ ...auditFilters, date_to: e.target.value })}
              className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Action Type</label>
            <select
              value={auditFilters.action_type}
              onChange={(e) => setAuditFilters({ ...auditFilters, action_type: e.target.value })}
              className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">All Actions</option>
              <option value="SEND_SMS">Send SMS</option>
              <option value="SEND_EMAIL">Send Email</option>
              <option value="CREATE_CONTACT">Create Contact</option>
              <option value="UPDATE_CONTACT">Update Contact</option>
              <option value="CREATE_OPPORTUNITY">Create Opportunity</option>
              <option value="TOKEN_ROTATED">Token Rotated</option>
              <option value="POLICY_UPDATED">Policy Updated</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">System</label>
            <select
              value={auditFilters.system}
              onChange={(e) => setAuditFilters({ ...auditFilters, system: e.target.value })}
              className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">All Systems</option>
              <option value="CRM">CRM</option>
              <option value="POD">POD</option>
              <option value="Directory">Directory</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Risk Level</label>
            <select
              value={auditFilters.risk_level}
              onChange={(e) => setAuditFilters({ ...auditFilters, risk_level: e.target.value })}
              className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">All Levels</option>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Status</label>
            <select
              value={auditFilters.status}
              onChange={(e) => setAuditFilters({ ...auditFilters, status: e.target.value })}
              className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">All Statuses</option>
              <option value="EXECUTED">Executed</option>
              <option value="FAILED">Failed</option>
              <option value="BLOCKED">Blocked</option>
              <option value="CANCELED">Canceled</option>
            </select>
          </div>
          <Button onClick={fetchAuditLogs} variant="outline" size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Apply
          </Button>
          {isAdmin && (
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-auto"
              onClick={handleExportAudit}
              disabled={exportingAudit}
              data-testid="export-audit-btn"
            >
              {exportingAudit ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
          )}
        </div>
      </div>
      
      {/* Log Table */}
      <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
        {loadingAudit ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Activity className="w-10 h-10 mb-3 opacity-50" />
            <p>No audit logs found</p>
            <p className="text-sm text-gray-500 mt-1">Agent activity will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black/20">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Date/Time</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">System</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Target</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Risk</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {auditLogs.map((log, idx) => (
                  <tr key={log.id || idx} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {log.user_name || log.user_email || 'System'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-white font-medium">
                        {log.action_type || log.tool_name}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "px-2 py-0.5 rounded text-xs font-medium",
                        log.system === 'CRM' && "bg-cyan-500/20 text-cyan-400",
                        log.system === 'POD' && "bg-purple-500/20 text-purple-400",
                        log.system === 'Directory' && "bg-green-500/20 text-green-400"
                      )}>
                        {log.system || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400 max-w-[200px] truncate">
                      {log.targets_summary?.display_name || log.targets_summary?.summary_line || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "px-2 py-0.5 rounded-full text-xs font-medium",
                        log.risk_level === 'HIGH' && "bg-red-500/20 text-red-400",
                        log.risk_level === 'MEDIUM' && "bg-amber-500/20 text-amber-400",
                        log.risk_level === 'LOW' && "bg-green-500/20 text-green-400"
                      )}>
                        {log.risk_level || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "px-2 py-0.5 rounded-full text-xs font-medium",
                        log.status === 'EXECUTED' && "bg-green-500/20 text-green-400",
                        log.status === 'FAILED' && "bg-red-500/20 text-red-400",
                        log.status === 'BLOCKED' && "bg-orange-500/20 text-orange-400",
                        log.status === 'CANCELED' && "bg-gray-500/20 text-gray-400"
                      )}>
                        {log.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
  
  // Render Advanced Tab (Admin Only)
  const renderAdvancedTab = () => (
    <div className="space-y-6">
      {/* Global Kill Switch */}
      <div className="bg-red-500/5 rounded-xl p-6 border border-red-500/20">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Global Kill Switch</h3>
            <p className="text-sm text-gray-400">
              Emergency disable for all agent executions system-wide
            </p>
          </div>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-black/20 rounded-lg">
          <div className="flex items-center gap-3">
            {globalKillSwitch ? (
              <>
                <PowerOff className="w-5 h-5 text-red-400" />
                <span className="text-red-400 font-medium">All agents are DISABLED</span>
              </>
            ) : (
              <>
                <Power className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">Agents operating normally</span>
              </>
            )}
          </div>
          <Button
            variant={globalKillSwitch ? "default" : "destructive"}
            onClick={() => setShowKillSwitchConfirm(true)}
            data-testid="global-kill-switch-btn"
          >
            {globalKillSwitch ? 'Enable All Agents' : 'Disable All Agents'}
          </Button>
        </div>
      </div>
      
      {/* Scope / Capabilities Summary */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Scope / Capabilities Summary</h3>
        <p className="text-sm text-gray-400 mb-4">Current integration permissions and capabilities</p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { name: 'Contacts', status: 'read/write', icon: Users },
            { name: 'Conversations', status: 'read/write', icon: MessageSquare },
            { name: 'Payments', status: capabilities?.payments ? 'enabled' : 'disabled', icon: CreditCard },
            { name: 'Phone Numbers', status: capabilities?.phone ? 'enabled' : 'disabled', icon: Phone },
            { name: 'Workflows', status: 'limited', icon: Zap },
            { name: 'Opportunities', status: 'read/write', icon: FileText }
          ].map(cap => (
            <div key={cap.name} className="flex items-center gap-3 p-3 bg-black/20 rounded-lg">
              <cap.icon className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-sm text-white">{cap.name}</p>
                <p className={cn(
                  "text-xs",
                  cap.status.includes('write') || cap.status === 'enabled' 
                    ? "text-green-400" 
                    : cap.status === 'disabled' 
                      ? "text-red-400"
                      : "text-amber-400"
                )}>
                  {cap.status}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Rotation History */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Rotation History</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-black/20 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Connected At</p>
            <p className="text-sm text-white">
              {formatDate(rotationHistory?.connected_at || integration?.connected_at)}
            </p>
          </div>
          <div className="bg-black/20 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Last Rotated At</p>
            <p className="text-sm text-white">
              {formatDate(rotationHistory?.last_rotated_at)}
            </p>
          </div>
          <div className="bg-black/20 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Last Verified At</p>
            <p className="text-sm text-white">
              {formatDate(rotationHistory?.last_verified_at || integration?.last_verified)}
            </p>
          </div>
          <div className="bg-black/20 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Last Rotation Event ID</p>
            <p className="text-sm text-white font-mono truncate">
              {rotationHistory?.last_rotation_event_id || 'N/A'}
            </p>
          </div>
        </div>
      </div>
      
      {/* System Flags */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">System Flags</h3>
        <div className="space-y-3">
          {[
            { id: 'debug_mode', label: 'Debug Mode', desc: 'Enable verbose logging for troubleshooting' },
            { id: 'maintenance_mode', label: 'Maintenance Mode', desc: 'Pause all non-critical operations' },
            { id: 'audit_verbose', label: 'Verbose Audit', desc: 'Log all actions including reads' }
          ].map(flag => (
            <div key={flag.id} className="flex items-center justify-between p-4 bg-black/20 rounded-lg">
              <div>
                <p className="text-white font-medium">{flag.label}</p>
                <p className="text-sm text-gray-400">{flag.desc}</p>
              </div>
              <Switch 
                checked={systemFlags[flag.id]} 
                onCheckedChange={(checked) => setSystemFlags({...systemFlags, [flag.id]: checked})}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Danger Zone */}
      <div className="bg-red-500/5 rounded-xl p-6 border border-red-500/20">
        <h3 className="text-lg font-semibold text-red-400 mb-4">Danger Zone</h3>
        <div className="space-y-3">
          <Button variant="outline" className="w-full justify-start text-red-400 border-red-500/30 hover:bg-red-500/10">
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All Audit Logs
          </Button>
          <Button variant="outline" className="w-full justify-start text-red-400 border-red-500/30 hover:bg-red-500/10">
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset All Tenant Policies to Default
          </Button>
        </div>
      </div>
    </div>
  );
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)] pt-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="min-h-[calc(100vh-64px)] bg-[#0a0a0f] pt-20">
        <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
              <Shield className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Security Settings</h1>
              <p className="text-gray-400">
                Control AGI Agent access, confirmations, and audit logging for your CRM, POD, and Directory.
              </p>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b border-white/10 pb-2 overflow-x-auto">
            {TABS.filter(tab => !tab.adminOnly || isAdmin).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
                  activeTab === tab.id
                    ? "bg-primary text-white"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                )}
                data-testid={`security-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.adminOnly && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-400">
                    Admin
                  </span>
                )}
              </button>
            ))}
          </div>
          
          {/* Tab Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 'integration' && renderIntegrationTab()}
              {activeTab === 'policy' && renderPolicyTab()}
              {activeTab === 'audit' && renderAuditTab()}
              {activeTab === 'advanced' && isAdmin && renderAdvancedTab()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      
      {/* Rotate Key Modal */}
      <AnimatePresence>
        {showRotateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowRotateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#16161d] rounded-2xl p-6 max-w-md w-full border border-white/10"
            >
              <h3 className="text-lg font-semibold text-white mb-2">Rotate API Key</h3>
              <p className="text-sm text-gray-400 mb-4">
                Enter your new API key. The old key will be invalidated immediately after verification.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">New API Key</label>
                  <input
                    type="password"
                    value={newApiKey}
                    onChange={(e) => setNewApiKey(e.target.value)}
                    placeholder="Enter new API key"
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500"
                    data-testid="new-api-key-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Confirm API Key</label>
                  <input
                    type="password"
                    value={confirmApiKey}
                    onChange={(e) => setConfirmApiKey(e.target.value)}
                    placeholder="Confirm new API key"
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500"
                    data-testid="confirm-api-key-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Location ID (optional)</label>
                  <input
                    type="text"
                    value={newLocationId}
                    onChange={(e) => setNewLocationId(e.target.value)}
                    placeholder={integration?.location_id || 'Keep existing'}
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500"
                  />
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={verifyAfterSave}
                    onChange={(e) => setVerifyAfterSave(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-500"
                  />
                  <span className="text-sm text-gray-300">Verify connection after saving</span>
                </label>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowRotateModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRotateKey}
                  disabled={rotatingKey || !newApiKey || newApiKey !== confirmApiKey}
                  className="flex-1"
                  data-testid="confirm-rotate-btn"
                >
                  {rotatingKey ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Rotate Key
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Disconnect Confirmation Modal */}
      <AnimatePresence>
        {showDisconnectConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDisconnectConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#16161d] rounded-2xl p-6 max-w-md w-full border border-red-500/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Disconnect Integration?</h3>
              </div>
              
              <p className="text-gray-400 mb-6">
                This will remove the CRM integration and disable all agent capabilities. 
                You will need to reconnect with a valid API key to use the agent again.
              </p>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDisconnectConfirm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDisconnect}
                  disabled={disconnectingIntegration}
                  className="flex-1"
                  data-testid="confirm-disconnect-btn"
                >
                  {disconnectingIntegration ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <X className="w-4 h-4 mr-2" />
                  )}
                  Disconnect
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Kill Switch Confirmation Modal */}
      <AnimatePresence>
        {showKillSwitchConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowKillSwitchConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#16161d] rounded-2xl p-6 max-w-md w-full border border-red-500/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  {activeTab === 'advanced' 
                    ? (globalKillSwitch ? 'Enable All Agents?' : 'Disable All Agents?')
                    : (policy.agent_enabled ? 'Disable Agent?' : 'Enable Agent?')
                  }
                </h3>
              </div>
              
              <p className="text-gray-400 mb-6">
                {activeTab === 'advanced' ? (
                  globalKillSwitch 
                    ? 'This will re-enable agent execution for all tenants system-wide.'
                    : 'This will immediately disable agent execution for ALL tenants. No agent will be able to perform any actions.'
                ) : (
                  policy.agent_enabled
                    ? 'This will disable agent execution. The agent will only be able to chat but cannot perform any actions.'
                    : 'This will re-enable agent execution capabilities.'
                )}
              </p>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowKillSwitchConfirm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant={
                    (activeTab === 'advanced' ? globalKillSwitch : !policy.agent_enabled) 
                      ? "default" 
                      : "destructive"
                  }
                  onClick={activeTab === 'advanced' ? handleToggleGlobalKillSwitch : handleToggleAgentExecution}
                  className="flex-1"
                >
                  {activeTab === 'advanced'
                    ? (globalKillSwitch ? 'Enable All' : 'Disable All')
                    : (policy.agent_enabled ? 'Disable Agent' : 'Enable Agent')
                  }
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}

// Connect Integration Form Component
function ConnectIntegrationForm({ onConnect, connecting, disabled }) {
  const [apiKey, setApiKey] = useState('');
  const [locationId, setLocationId] = useState('');
  const [showKey, setShowKey] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (apiKey && locationId && !disabled) {
      onConnect(apiKey, locationId);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20 mb-4">
        <p className="text-sm text-amber-400 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          Connect your CRM account to enable AGI Workhorse™ capabilities
        </p>
      </div>
      
      <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20 mb-4">
        <p className="text-xs text-blue-400 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span><strong>Important:</strong> Use a <span className="text-white font-semibold">Private Integration API Key</span> (not a Legacy API key). Private Integrations provide better security and more features.</span>
        </p>
      </div>
      
      <div>
        <label className="block text-sm text-gray-400 mb-2">Private Integration API Key</label>
        <div className="relative">
          <input
            type={showKey ? "text" : "password"}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Enter your Private Integration API key"
            className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 pr-12"
            required
            disabled={disabled}
            data-testid="api-key-input"
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
          >
            {showKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>
      
      <div>
        <label className="block text-sm text-gray-400 mb-2">Location ID</label>
        <input
          type="text"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          placeholder="Enter your Location ID"
          className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500"
          required
          disabled={disabled}
          data-testid="location-id-input"
        />
      </div>
      
      <Button
        type="submit"
        disabled={connecting || !apiKey || !locationId || disabled}
        className="w-full"
        data-testid="connect-integration-btn"
      >
        {connecting ? (
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
        ) : (
          <Key className="w-4 h-4 mr-2" />
        )}
        Connect Integration
      </Button>
    </form>
  );
}
